import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { Add as AddIcon, MoreVert as MoreIcon, PersonAddAlt1Outlined as InviteIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigationIntent } from '../hooks/useNavigationIntent';
import { useFinancialContexts } from '../contexts/ContextsContext';
import { contextApi, ContextBalances, CreateContextData, FinancialContext } from '../services/contextApi';
import { ContextFormDialog } from '../components/contexts/ContextFormDialog';
import { ContextMembersDialog } from '../components/contexts/ContextMembersDialog';
import ConnectTelegramDialog from '../components/contexts/ConnectTelegramDialog';
import { canLinkTelegramGroup } from '../utils/contextPermissions';
import { ContextBadge, useGroups } from '../components/Layout/ContextPicker';
import { PersonDot } from '../components/dashboard/home/ui';
import { useAuth } from '../contexts/AuthContext';
import { BOT_USERNAME } from '../config/telegram';
import { formatMoneyCents } from '../theme/chart';
import { personColor, tokens } from '../theme/tokens';
import { richText } from '../components/landing/richText';

type Pending = { kind: 'delete' | 'leave'; group: FinancialContext } | null;

const card = {
  bgcolor: tokens.surface,
  border: `1px solid ${tokens.line}`,
  borderRadius: `${tokens.radius.card}px`,
} as const;

/** One group: who is in it, where the user stands, and the way in. */
const GroupCard: React.FC<{
  group: FinancialContext;
  index: number;
  balances?: ContextBalances;
  currentUserId?: string;
  isOwner: boolean;
  onOpen: () => void;
  onMembers: () => void;
  onTransactions: () => void;
  onEdit: () => void;
  onRemove: () => void;
  /** Offered while the group has no Telegram group and the user may link one. */
  onConnect?: () => void;
}> = ({ group, index, balances, currentUserId, isOwner, onOpen, onMembers, onTransactions, onEdit, onRemove, onConnect }) => {
  const { t, i18n } = useTranslation('contexts');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const members = balances?.members ?? [];
  const money = (value: number) => formatMoneyCents(value, group.defaultCurrency, i18n.language);

  const receivable = balances?.pairs.filter((pair) => pair.toUserId === currentUserId).reduce((sum, pair) => sum + pair.amount, 0) ?? 0;
  const payable = balances?.pairs.filter((pair) => pair.fromUserId === currentUserId).reduce((sum, pair) => sum + pair.amount, 0) ?? 0;
  const net = Math.round((receivable - payable) * 100) / 100;
  // A group nobody has spent in yet isn't "settled": what it lacks is Telegram.
  const untouched = Boolean(balances && balances.pairs.length === 0 && balances.members.every((member) => !member.paid && !member.owed));
  const standing = !balances
    ? null
    : untouched && !group.telegramChats?.length
      ? { text: t(canLinkTelegramGroup(group.memberRole) ? 'card.notConnected' : 'card.notConnectedMember'), bg: tokens.bg, color: tokens.ink2 }
      : net > 0
        ? { text: t('card.toReceive', { amount: money(net) }), bg: tokens.accentSoft, color: tokens.accentDeep }
        : net < 0
          ? { text: t('card.toPay', { amount: money(-net) }), bg: tokens.warnSoft, color: tokens.warn }
          : { text: t('card.settled'), bg: tokens.bg, color: tokens.ink2 };

  const names = members.map((member) => (member.userId === currentUserId ? t('card.you') : member.firstName));
  const peopleLine = names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} ${t('card.and')} ${names[names.length - 1]}`;
  const pending = members.filter((member) => member.pending).length;

  const close = () => setAnchor(null);

  return (
    <Box component='li' sx={{ ...card, p: { xs: 2.25, md: 2.75 }, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <ContextBadge group={group} index={index} size={44} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 19, fontWeight: 700, overflowWrap: 'anywhere' }}>{group.name}</Box>
          <Box component='span' sx={{ fontSize: 13.5, color: tokens.ink3 }}>
            {[t(`types.${group.type}`, { defaultValue: '' }), members.length ? t('card.people', { count: members.length }) : ''].filter(Boolean).join(' · ')}
          </Box>
        </Box>
        <IconButton aria-label={t('card.moreFor', { name: group.name })} onClick={(event) => setAnchor(event.currentTarget)} sx={{ width: 40, height: 40, color: tokens.ink3, mt: -0.5 }}>
          <MoreIcon />
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={() => { close(); onTransactions(); }}><ListItemText>{t('actions.viewTransactions')}</ListItemText></MenuItem>
          {isOwner && <MenuItem onClick={() => { close(); onEdit(); }}><ListItemText>{t('actions.edit')}</ListItemText></MenuItem>}
          <MenuItem onClick={() => { close(); onRemove(); }} sx={{ color: tokens.danger }}>
            <ListItemText>{isOwner ? t('actions.delete') : t('actions.leave')}</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {group.description && <Box component='p' sx={{ m: 0, fontSize: 14, color: tokens.ink2, lineHeight: 1.5 }}>{group.description}</Box>}

      {standing ? (
        <Box sx={{ px: 1.75, py: 1.25, borderRadius: '14px', bgcolor: standing.bg, color: standing.color, fontSize: 14.5, fontWeight: 600 }}>
          {standing.text}
        </Box>
      ) : (
        <Skeleton variant='rounded' height={44} sx={{ borderRadius: '14px' }} />
      )}

      {members.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexShrink: 0 }} aria-hidden>
            {members.slice(0, 5).map((member, position) => {
              const person = personColor(position);
              return (
                <Box key={member.userId} sx={{ ml: position ? '-8px' : 0 }}>
                  <PersonDot name={member.firstName} color={person.color} tint={person.tint} size={30} ring />
                </Box>
              );
            })}
          </Box>
          <Box component='span' sx={{ fontSize: 13.5, color: tokens.ink2, minWidth: 0 }}>
            {peopleLine}
            {pending > 0 && <Box component='span' sx={{ color: tokens.ink3 }}>{` · ${t('card.pending', { count: pending })}`}</Box>}
          </Box>
        </Box>
      )}

      {/* The primary action first: setting the group up on Telegram, while it isn't, comes before opening it. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto', pt: 0.5 }}>
        {onConnect && (
          <Button variant='contained' startIcon={<TelegramIcon />} onClick={onConnect} sx={{ minHeight: 44 }}>{t('connect.cardButton')}</Button>
        )}
        <Button variant={onConnect ? 'outlined' : 'contained'} onClick={onOpen} sx={{ minHeight: 44 }}>{t('card.open')}</Button>
        <Button variant='outlined' startIcon={<InviteIcon />} onClick={onMembers} sx={{ minHeight: 44 }}>{t('card.members')}</Button>
      </Box>
    </Box>
  );
};

/** The two ways a group starts: here, then connected to Telegram in two taps; or from Telegram with the bot. */
const HowTo: React.FC<{ onCreate: () => void }> = ({ onCreate }) => {
  const { t } = useTranslation('contexts');
  const ways = [
    { title: t('howTo.app.title'), text: t('howTo.app.text') },
    { title: t('howTo.telegram.title'), text: richText(t('howTo.telegram.text', { bot: `@${BOT_USERNAME}` })) },
  ];
  return (
    <Box component='section' aria-label={t('howTo.title')} sx={{ ...card, p: { xs: 2.25, md: 2.75 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 18, fontWeight: 650 }}>{t('howTo.title')}</Box>
      <Box component='ol' sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {ways.map((way, index) => (
          <Box component='li' key={way.title} sx={{ display: 'flex', gap: 1.5 }}>
            <Box component='span' aria-hidden sx={{ width: 30, height: 30, flexShrink: 0, borderRadius: '50%', bgcolor: tokens.accentSoft, color: tokens.accent, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {index + 1}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <strong>{way.title}</strong>
              <Box component='span' sx={{ fontSize: 14, color: tokens.ink2, lineHeight: 1.5 }}>{way.text}</Box>
            </Box>
          </Box>
        ))}
      </Box>
      <Button variant='outlined' startIcon={<AddIcon />} onClick={onCreate} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>{t('actions.create')}</Button>
    </Box>
  );
};

/**
 * Grupos: each group with its people and where the user stands, the way
 * into its Início, its members, and how to start a new one — from the
 * Telegram group with Fin, or here.
 */
const ContextsPage: React.FC = () => {
  const { t } = useTranslation('contexts');
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { isLoading, error, reload, createContext, updateContext, deleteContext, leaveContext, selectContext } = useFinancialContexts();
  const groups = useGroups();
  const userId = authState.user?.id;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FinancialContext | null>(null);
  const [membersFor, setMembersFor] = useState<FinancialContext | null>(null);
  // A group just created here goes straight on to Telegram; an older one from its card.
  const [connecting, setConnecting] = useState<{ group: FinancialContext } | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [balances, setBalances] = useState<ContextBalances[] | null>(null);
  const [balancesVersion, setBalancesVersion] = useState(0);

  // "Criar grupo" from the context picker, the Início or the onboarding lands here with the form open.
  useNavigationIntent('createGroup', true, () => {
    setEditing(null);
    setFormOpen(true);
  });

  useEffect(() => {
    let cancelled = false;
    contextApi
      .getAllBalances()
      .catch(() => [])
      .then((result) => {
        if (!cancelled) setBalances(result);
      });
    return () => {
      cancelled = true;
    };
  }, [balancesVersion, groups.length]);

  const create = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const submit = async (data: CreateContextData) => {
    if (editing) await updateContext(editing.id, data);
    else setConnecting({ group: await createContext(data) });
  };

  const confirmRemoval = async () => {
    if (!pending) return;
    setActionError(null);
    try {
      if (pending.kind === 'delete') await deleteContext(pending.group.id);
      else await leaveContext(pending.group.id);
      setPending(null);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err.message);
      setPending(null);
    }
  };

  const openIn = (group: FinancialContext, path: string) => {
    selectContext(group.id);
    navigate(path);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 640 }}>
          <Box component='h1' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 26, md: 30 }, fontWeight: 700, letterSpacing: '-0.025em' }}>
            {t('title')}
          </Box>
          <Box component='p' sx={{ m: 0, fontSize: 15, color: tokens.ink2, lineHeight: 1.5 }}>{t('subtitle')}</Box>
        </Box>
        <Button variant='contained' startIcon={<AddIcon />} onClick={create} sx={{ minHeight: 44 }}>
          {t('actions.create')}
        </Button>
      </Box>

      {(error || actionError) && (
        <Alert severity='error' onClose={() => setActionError(null)}>
          {actionError || error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 2.5 }}>
          {[0, 1].map((index) => <Skeleton key={index} variant='rounded' height={260} sx={{ borderRadius: `${tokens.radius.card}px` }} />)}
        </Box>
      ) : groups.length === 0 ? (
        <Box component='section' aria-label={t('empty.title')} sx={{ ...card, p: { xs: 2.5, md: 4 }, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
          <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 21, md: 24 }, fontWeight: 700 }}>{t('empty.title')}</Box>
          <Box component='p' sx={{ m: 0, color: tokens.ink2, maxWidth: 620, lineHeight: 1.55 }}>{t('empty.description')}</Box>
        </Box>
      ) : (
        <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: { xs: 2, md: 2.5 } }}>
          {groups.map((group, index) => (
            <GroupCard
              key={group.id}
              group={group}
              index={index}
              balances={balances ? balances.find((entry) => entry.contextId === group.id) ?? { contextId: group.id, contextName: group.name, currency: group.defaultCurrency, members: [], pairs: [] } : undefined}
              currentUserId={userId}
              isOwner={group.ownerId === userId}
              onOpen={() => openIn(group, '/dashboard')}
              onMembers={() => setMembersFor(group)}
              onTransactions={() => openIn(group, '/transactions')}
              onEdit={() => {
                setEditing(group);
                setFormOpen(true);
              }}
              onRemove={() => setPending({ kind: group.ownerId === userId ? 'delete' : 'leave', group })}
              onConnect={
                !group.telegramChats?.length && canLinkTelegramGroup(group.memberRole)
                  ? () => setConnecting({ group })
                  : undefined
              }
            />
          ))}
        </Box>
      )}

      {!isLoading && <HowTo onCreate={create} />}

      <ContextFormDialog
        open={formOpen}
        context={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={submit}
      />
      <ConnectTelegramDialog
        open={Boolean(connecting)}
        context={connecting ? groups.find((group) => group.id === connecting.group.id) ?? connecting.group : null}
        onClose={() => setConnecting(null)}
      />
      <ContextMembersDialog
        open={Boolean(membersFor)}
        context={membersFor}
        onClose={() => {
          setMembersFor(null);
          reload();
          setBalancesVersion((version) => version + 1);
        }}
      />
      <Dialog open={Boolean(pending)} onClose={() => setPending(null)} maxWidth='xs' fullWidth>
        <DialogTitle>{pending?.kind === 'delete' ? t('confirm.deleteTitle', { name: pending.group.name }) : t('confirm.leaveTitle', { name: pending?.group.name ?? '' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{pending?.kind === 'delete' ? t('confirm.deleteText') : t('confirm.leaveText')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>{t('actions.cancel')}</Button>
          <Button variant='contained' color='error' onClick={confirmRemoval}>
            {pending?.kind === 'delete' ? t('actions.delete') : t('actions.leave')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContextsPage;
